import { MateSpriteAction, MateSpriteDirection } from "@/enums/game";
import { Mate } from "@/types/entity";
import { MateSprite, MateSpriteAnimation, MateSpriteMode } from "@/types/game";
import GridEngine, { Direction } from "grid-engine";

// @region 索引Key拼接方法 Start
export const getSpriteKey = (sprite: MateSprite) => {
  return sprite.id!.toString();
};

export const getAvatarKey = (mate: Mate) => {
  return "avatar-" + mate.id;
};

export const getSpriteAnimKeyByParam = (
  sprite: MateSprite,
  action: MateSpriteAction,
  direction: MateSpriteDirection
) => {
  const key = getSpriteKey(sprite);
  return `${key}_${action}_${direction}`;
};

export const getSpriteAnimKeyByMateAnimation = (
  sprite: MateSprite,
  anims: MateSpriteAnimation
) => {
  const key = getSpriteKey(sprite);
  return `${key}_${anims.action}_${anims.direction}`;
};
// @endregion

// @region Sprite加载方法
/**
 * 目前正在使用的，加载精灵图的方法
 * @param scene
 * @param sprite
 */
export const loadImageFromSperateFrames = (
  scene: Phaser.Scene,
  sprite: MateSprite
) => {
  sprite.animationzz?.forEach((anims) => {
    scene.load.spritesheet(
      getSpriteAnimKeyByMateAnimation(sprite, anims),
      anims.media?.url,
      {
        frameWidth: 48,
        frameHeight: 96,
      }
    );
  });
};

export const loadSpriteIcon = (scene: Phaser.Scene) => {
  scene.load.spritesheet("icon", "emotes.png", {
    frameWidth: 32,
    frameHeight: 32,
  });
};

export const loadIndicator = (scene: Phaser.Scene) => {
  scene.load.spritesheet("indicator", "indicator_h.png", {
    frameWidth: 16,
    frameHeight: 32,
  });
};

export const loadAvatar = (mate: Mate, scene: Phaser.Scene) => {
  if (!mate.mediaUrl) return;
  scene.load.image(getAvatarKey(mate), mate.mediaUrl);
};
// @endregion

export interface IMateCharacter {
  mateSprite: MateSprite;
  mate: Mate;
  container: Phaser.GameObjects.Container;
  sprite: Phaser.GameObjects.Sprite;
  bulb: Phaser.GameObjects.Sprite;
  shock: Phaser.GameObjects.Sprite;
  indicator: Phaser.GameObjects.Sprite;
  textBubble?: Phaser.GameObjects.Text;
}

export const mateCharacterProvider = (params: {
  scene: Phaser.Scene;
  x: number;
  y: number;
  mateSprite: MateSprite;
  gridEngine: GridEngine;
  options?: {
    speed?: number;
    scale?: number;
  };
}): IMateCharacter => {
  const { scene, x, y, mateSprite, gridEngine, options } = params;
  const { speed = 1, scale = 1 } = options || {};
  const container = scene.add.container(x, y);
  const sprite: Phaser.GameObjects.Sprite = scene.add.sprite(
    0,
    0,
    `${getSpriteKey(mateSprite)}_${MateSpriteAction.Running}_${
      MateSpriteDirection.Down
    }`,
    0
  );
  const base = mateSprite.mode === MateSpriteMode.Compose ? 85 / 64 : 1;
  sprite.setScale(base * scale); // 48*48 -> 32*32

  const bulb = supplyBulb(scene, mateSprite.mode!, {
    scale: options?.scale,
  });
  const shock = supplyShock(scene, mateSprite.mode!, {
    scale: options?.scale,
  });
  const indicator = supplyIndicator(scene, mateSprite.mode!, {
    scale: options?.scale,
  });
  container.add(sprite);
  container.add(bulb);
  container.add(shock);
  container.add(indicator);
  if (mateSprite.mode === MateSpriteMode.Compose) {
    supply8FrameAnimation(scene, mateSprite);
  } else {
    supply4FrameAnimation(scene, mateSprite);
  }
  gridEngine.addCharacter({
    id: getSpriteKey(mateSprite),
    sprite,
    container,
    startPosition: { x, y },
    speed,
  });

  // Y-sort by bottom Y each frame to ensure correct overlap with furniture
  const CHARACTER_BASE_DEPTH = 100;
  const updateDepth = () => {
    const bottom = container.getBounds().bottom;
    container.setDepth(CHARACTER_BASE_DEPTH + bottom);
  };
  updateDepth();
  scene.events.on("update", updateDepth);
  scene.events.on(Phaser.Scenes.Events.SHUTDOWN, () => {
    scene.events.off("update", updateDepth);
  });

  return {
    mateSprite,
    mate: mateSprite.mate!,
    container,
    sprite,
    bulb,
    shock,
    indicator,
  };
};

const supplyBulb = (
  scene: Phaser.Scene,
  mode: MateSpriteMode,
  options?: {
    scale?: number;
  }
): Phaser.GameObjects.Sprite => {
  const { scale = 1 } = options ?? {};
  const bulb = scene.add.sprite(0, -4, "icon", 49);
  if (mode === MateSpriteMode.Compose) {
    bulb.setPosition(32 * scale, 16 * scale);
  } else {
    bulb.setPosition(24 * scale, 0 * scale);
  }
  bulb.visible = false;
  return bulb;
};
const supplyShock = (
  scene: Phaser.Scene,
  mode: MateSpriteMode,
  options?: {
    scale?: number;
  }
): Phaser.GameObjects.Sprite => {
  const { scale = 1 } = options ?? {};
  const bulb = scene.add.sprite(8, -4, "icon", 1);
  bulb.setScale(scale);
  if (mode === MateSpriteMode.Compose) {
    bulb.setPosition(32 * scale, 16 * scale);
  } else {
    bulb.setPosition(24 * scale, 0 * scale);
  }
  bulb.visible = false;
  return bulb;
};
const supplyIndicator = (
  scene: Phaser.Scene,
  mode: MateSpriteMode,
  options?: {
    scale?: number;
  }
): Phaser.GameObjects.Sprite => {
  const { scale = 1 } = options ?? {};
  const indicator = scene.add.sprite(0, 0, "indicator", 0);
  indicator.setOrigin(0.5, 0.5);
  if (mode === MateSpriteMode.Compose) {
    indicator.setPosition(32 * scale, 64 * scale);
  } else {
    indicator.setPosition(24 * scale, 48 * scale);
  }
  indicator.setScale(3 * scale);
  indicator.setVisible(false);
  return indicator;
};

const supply4FrameAnimation = (scene: Phaser.Scene, mateSprite: MateSprite) => {
  const frameKey = getSpriteKey(mateSprite);
  scene.anims.create({
    key: `${frameKey}_idle`,
    frames: [
      {
        key: getSpriteAnimKeyByParam(
          mateSprite,
          MateSpriteAction.Running,
          MateSpriteDirection.Down
        ),
        frame: 0,
      },
    ],
    frameRate: 17,
  });
  const directions: MateSpriteDirection[] = [
    MateSpriteDirection.Down,
    MateSpriteDirection.Up,
    MateSpriteDirection.Left,
    MateSpriteDirection.Right,
  ];
  directions.forEach((direction) => {
    scene.anims.create({
      key: `${frameKey}_${direction}`,
      frames: scene.anims.generateFrameNumbers(
        getSpriteAnimKeyByParam(
          mateSprite,
          MateSpriteAction.Running,
          direction
        ),
        {
          start: 0,
          end: 3,
        }
      ),
      frameRate: 4,
      repeat: -1,
    });
    scene.anims.create({
      key: `${frameKey}_${direction}_idle`,
      frames: [
        {
          key: getSpriteAnimKeyByParam(
            mateSprite,
            MateSpriteAction.Running,
            direction
          ),
          frame:
            direction === MateSpriteDirection.Down ||
            direction === MateSpriteDirection.Up
              ? 0
              : 1,
        },
      ],
      frameRate: 1,
      repeat: -1,
    });
  });
};

const supply8FrameAnimation = (scene: Phaser.Scene, mateSprite: MateSprite) => {
  const frameKey = getSpriteKey(mateSprite);
  scene.anims.create({
    key: `${frameKey}_idle`,
    frames: [
      {
        key: getSpriteAnimKeyByParam(
          mateSprite,
          MateSpriteAction.Running,
          MateSpriteDirection.Down
        ),
        frame: 0,
      },
    ],
    frameRate: 17,
  });
  const directions: MateSpriteDirection[] = [
    MateSpriteDirection.Down,
    MateSpriteDirection.Up,
    MateSpriteDirection.Left,
    MateSpriteDirection.Right,
  ];
  directions.forEach((direction) => {
    scene.anims.create({
      key: `${frameKey}_${direction}`,
      frames: scene.anims.generateFrameNumbers(
        getSpriteAnimKeyByParam(
          mateSprite,
          MateSpriteAction.Running,
          direction
        ),
        {
          start: 0,
          end: 7,
        }
      ),
      frameRate: 8,
      repeat: -1,
    });
    scene.anims.create({
      key: `${frameKey}_${direction}_idle`,
      frames: [
        {
          key: getSpriteAnimKeyByParam(
            mateSprite,
            MateSpriteAction.Running,
            direction
          ),
          frame:
            direction === MateSpriteDirection.Down ||
            direction === MateSpriteDirection.Up
              ? 0
              : 2,
        },
      ],
      frameRate: 1,
      repeat: 0,
    });
  });
};

export const getGEDirectionToSpriteDirection = (
  direction: Direction
): MateSpriteDirection => {
  if (direction === "up") {
    return MateSpriteDirection.Up;
  } else if (direction === "down") {
    return MateSpriteDirection.Down;
  } else if (direction === "left") {
    return MateSpriteDirection.Left;
  } else if (direction === "right") {
    return MateSpriteDirection.Right;
  } else {
    return MateSpriteDirection.Down;
  }
};

export const PlayerCharacterUtils = {
  provideDebugCharacter: (
    params: Parameters<typeof mateCharacterProvider>[0]
  ) => {
    return mateCharacterProvider(params);
  },
  supplyControl: (
    scene: Phaser.Scene,
    gridEngine: GridEngine,
    mateSprite: MateSprite
  ) => {
    const cursors = scene.input?.keyboard?.createCursorKeys();
    if (!cursors) return;

    if (cursors.left.isDown) {
      gridEngine.move(getSpriteKey(mateSprite), Direction.LEFT);
    } else if (cursors.right.isDown) {
      gridEngine.move(getSpriteKey(mateSprite), Direction.RIGHT);
    } else if (cursors.up.isDown) {
      gridEngine.move(getSpriteKey(mateSprite), Direction.UP);
    } else if (cursors.down.isDown) {
      gridEngine.move(getSpriteKey(mateSprite), Direction.DOWN);
    }
  },
};
